import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface DriverContract {
    id: number;
    current_status: string | null;
    vehicle_registration: string | null;
    vehicle_make: string | null;
    vehicle_model: string | null;
}

interface UseDriverContractResult {
    activeContract: DriverContract | null;
    allContracts: DriverContract[];
    driverId: number | null;
    isLoading: boolean;
    error: Error | null;
}

export function useDriverContract(): UseDriverContractResult {
    const { user } = useAuth();

    // Step 1: Get driver_id from profiles table
    const { data: profile, isLoading: profileLoading, error: profileError } = useQuery({
        queryKey: ['driver-profile', user?.id],
        queryFn: async () => {
            if (!user?.id) return null;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data, error } = await (supabase as any)
                .from('profiles')
                .select('driver_id')
                .eq('user_id', user.id)
                .maybeSingle();
            if (error) throw error;
            return data;
        },
        enabled: !!user?.id,
    });

    const driverId = profile?.driver_id ?? null;

    // Step 2: Get all contracts for this driver
    const { data: contracts, isLoading: contractsLoading, error: contractsError } = useQuery({
        queryKey: ['driver-contracts', driverId],
        queryFn: async () => {
            if (!driverId) return [];

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data: contractRows, error } = await (supabase as any)
                .from('contracts')
                .select('id, driver_id, vehicle_id, contract_updates(status, created_at)')
                .eq('driver_id', driverId);

            if (error) throw error;

            if (!contractRows || contractRows.length === 0) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return contractRows.map((c: any) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const latestStatus = c.contract_updates
                    ? [...c.contract_updates].sort(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                    )[0]?.status ?? null
                    : null;

                return {
                    id: c.id,
                    current_status: latestStatus,
                    vehicle_registration: null,
                    vehicle_make: null,
                    vehicle_model: null,
                } as DriverContract;
            });
        },
        enabled: !!driverId,
    });

    // Find the active contract (IN_PROGRESS status)
    const activeContract = contracts?.find((c: DriverContract) => c.current_status === 'IN_PROGRESS')
        || contracts?.[0]
        || null;

    return {
        activeContract,
        allContracts: contracts || [],
        driverId,
        isLoading: profileLoading || contractsLoading,
        error: (profileError || contractsError) as Error | null,
    };
}
